<template>
  <control-base
    :config="conf"
    :dataType="dataType"
    :parentName="$options.name"
    @loaded="$emit('loaded', $event)"
    ref="base"
    campaignTypes="in"
  >
    <template v-slot:filters>
      <div class="flex-full">
        <p>Seperate queues:</p>
        <input type="checkbox" v-model="conf.split" />
      </div>

      <multi-selection-list
        caption="Extra queue stats to show (when not using split mode the total value for all selected queues will be shown):"
        :items="statsList"
        @add="addStat"
        @remove="removeStat"
      />

      <div class="flex-full" v-if="conf.split">
        <p>Ordered By:</p>
        <drop-down
          class="w-full"
          :options="sortOptions"
          :selected="conf.sortBy"
          placeholder="Ordered By"
          @updateOption="selectSort"
          :closeOnOutsideClick="true"
        />
      </div>

      <div class="flex-full">
        <p>Flash when there's a queued call (turns off static colours):</p>
        <input type="checkbox" @change="invert('flash')" v-model="conf.flash" />
      </div>

      <div
        v-if="conf.flash"
        style="border: 1px solid gray; padding: 5px; margin-top: 5px"
      >
        <div class="flex-full">
          <p>First flash colour:</p>
          <colour-picker
            :color="conf.flashColors.first"
            v-model="conf.flashColors.first"
          />
        </div>

        <div class="flex-full">
          <p>Second flash colour:</p>
          <colour-picker
            :color="conf.flashColors.second"
            v-model="conf.flashColors.second"
          />
        </div>
      </div>
      <div class="flex-full">
        <p style="margin-right: 10px">
          Static background colour change when calls queueing (turns off flash):
        </p>
        <input
          type="checkbox"
          @change="invert('fixed')"
          v-model="conf.fixedColor"
        />
      </div>

      <div
        v-if="conf.fixedColor"
        style="
          border: 1px solid gray;
          padding: 5px;
          margin-top: 5px;
          display: flex;
          flex-direction: column;
          align-items: center;
        "
      >
        <div
          v-for="(color, index) in conf.fixedColors"
          :key="index"
          style="display: flex; margin: 5px 0; align-items: center"
        >
          <select v-model="color.filter" style="margin: 0 5px">
            <option value = "0">When queued calls greater than or equal to:</option>
            <option value = "1">When queued calls equal to:</option>
            <option value = "2">When queued calls less than or equal to:</option>
            <option value = "3">Default</option>
          </select>
          <input
            style="width: 50px; margin: 0 5px"
            type="number"
            min="1"
            v-model="color.volume"
            v-if="color.filter != 3"
          />
          <p>Use color:</p>
          <colour-picker :color="color.color" v-model="color.color" />
        </div>
        <div
          style="
            padding: 5px 20px;
            background-color: #0186c9;
            color: white;
            cursor: pointer;
          "
          @click="addColor"
        >
          Add colour
        </div>
      </div>
    </template>
    <template v-slot:control>
      <div
        ref="queue-container"
        class="default-container"
        :class="{ scroll: conf.split }"
      >
        <div
          v-if="!split"
          :style="getType(totalQueuedCalls)"
          class="combined-container"
        >
          <div
            class="section"
            :style="{ maxHeight: maxHeight }"
            v-if="displayStat('CallsInQueue')"
          >
            <div class="h-half">
              <p class="no-wrap" v-multi-ref:resize>Calls Queueing</p>
            </div>
            <div class="h-half">
              <p class="bold" v-multi-ref:resize>{{ totalQueuedCalls }}</p>
            </div>
          </div>
          <div
            class="section"
            :style="{ maxHeight: maxHeight }"
            v-if="displayStat('OldestCallInQueue')"
          >
            <div class="h-half">
              <p class="no-wrap" v-multi-ref:resize>Oldest call:</p>
            </div>
            <div class="h-half">
              <p class="bold" v-multi-ref:resize>
                {{ oldestIndividualCall ? oldestIndividualCall : "0h 0m 0s" }}
              </p>
            </div>
          </div>
          <div class="section" :style="{ maxHeight: maxHeight }">
            <p
              class="detail no-wrap"
              :style="{ maxHeight: maxDetailHeight }"
              v-if="displayStat('TotalCalls')"
              v-multi-ref:resize-detail
            >
              Total Calls: {{ totalCalls }}
            </p>
            <p
              class="detail no-wrap"
              :style="{ maxHeight: maxDetailHeight }"
              v-if="displayStat('AbandonedCalls')"
              v-multi-ref:resize-detail
            >
              Abandoned: {{ abandonedCalls }}
            </p>
            <p
              class="detail no-wrap"
              :style="{ maxHeight: maxDetailHeight }"
              v-if="displayStat('AbandonRate')"
              v-multi-ref:resize-detail
            >
              Abandon Rate: {{ abandonRate }}
            </p>
          </div>
        </div>

        <div v-else class="split-container">
          <queue-row
            v-for="queue in allQueues"
            :key="queue.queueNumber"
            :queue="queue"
            :conf="conf"
            @resized="count"
            :shouldResize="shouldResize"
            :height="splitHeight"
            stagger="50"
            :sectionHeight="sectionHeight"
          ></queue-row>
        </div>
      </div>
    </template>
  </control-base>
</template>

<script>
import { QueueRow } from "@/components";

export default {
  name: "QueueingCalls",

  components: {
    QueueRow,
  },

  props: {
    config: {
      required: true,
      value: Object,
    },

    shouldResize: {
      required: false,
      value: Boolean,
    },
  },

  data() {
    return {
      dataType: "queue",
      resizedRows: 0,
      statsToDisplay: [
        "CallsInQueue",
        "AbandonedCalls",
        "AbandonRate",
        "OldestCallInQueue",
        "TotalCalls",
      ],
      height: 0,
      sortOptions: [
        "None",
        "Queueing Calls",
        "Total Calls",
        "Abandoned",
        "Abandon Rate",
      ],
    };
  },

  mounted() {
    this.registerDependencies();
    if (!this.conf.split)
      setTimeout(
        () =>
          this.$nextTick(() => {
            this.doResize();
          }),
        1000
      );

    this.height = this.$refs["queue-container"].clientHeight;
    this.$emit("resized");
  },

  watch: {
    resize() {
      if (!this.conf.split)
        this.$nextTick(() => {
          this.doResize();
        });

      this.height = this.$refs["queue-container"].clientHeight;
    },

    maxHeight() {
      this.$nextTick(() => {
        this.doResize();
      });
    },

    maxDetailHeight() {
      this.$nextTick(() => {
        this.doResize();
      });
    },

    statsList: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.doResize();
        });
      },
    },
  },

  computed: {
    maxHeight() {
      var sections = [
        this.isShowingDetails,
        this.isShowingCallsInQueue,
        this.isShowingOldestCall,
      ].filter(Boolean).length;
      let height = 100 / sections;
      if (height == Infinity) height = 33;
      return height + "%";
    },

    maxDetailHeight() {
      let detailStats = ["AbandonedCalls", "AbandonRate", "TotalCalls"];
      var details = this.conf.statsToDisplay?.filter((x) =>
        detailStats.includes(x)
      ).length;
      if (details == null) return "33%";
      return 100 / details + "%";
    },

    isShowingDetails() {
      if (!this.conf.statsToDisplay) return true;
      return (
        this.conf.statsToDisplay.filter((x) =>
          ["AbandonedCalls", "AbandonRate", "TotalCalls"].includes(x)
        ).length > 0
      );
    },

    isShowingCallsInQueue() {
      if (!this.conf.statsToDisplay) return true;
      return this.conf.statsToDisplay.includes("CallsInQueue");
    },

    isShowingOldestCall() {
      if (!this.conf.statsToDisplay) return true;
      return this.conf.statsToDisplay.includes("OldestCallInQueue");
    },

    sectionHeight() {
      if (!this.conf.statsToDisplay) return "100%";
      if (
        this.isShowingDetails &&
        (this.isShowingCallsInQueue || this.isShowingOldestCall)
      )
        return "33%";
      else if (
        !this.isShowingDetails &&
        !(this.isShowingCallsInQueue || this.isShowingOldestCall)
      )
        return "100%";
      else return "50%";
    },

    resize() {
      return this.shouldResize;
    },

    conf() {
      return this.config;
    },

    statsList() {
      return this.statsToDisplay.map((x) => ({
        name: x,
        selected: this.conf.statsToDisplay
          ? this.conf.statsToDisplay.includes(x)
          : true,
      }));
    },

    splitHeight() {
      var splitHeight = this.height / this.allQueues.length;
      if (splitHeight < 50) return 50;
      else return splitHeight;
    },

    split() {
      return this.conf.split;
    },

    totalQueuedCalls() {
      return this.allQueues.map((x) => x.calls).reduce(this.sum, 0);
    },

    totalCalls() {
      return this.allQueues.map((x) => x.totalCalls).reduce(this.sum, 0);
    },

    abandonedCalls() {
      return this.allQueues.map((x) => x.abandoned).reduce(this.sum, 0);
    },

    abandonRate() {
      return ((this.abandonedCalls / this.totalCalls) * 100).toFixed(2) + "%";
    },

    oldestIndividualCall() {
      var queues = this.allQueues.filter((x) => x.oldestDT != null);
      if (queues.length == 0) return "";
      var oldest = queues
        .map((x) => new Date(Date.parse(x.oldestDT)))
        .sort((a, b) => (a > b ? 1 : -1))[0];
      return this.toHMS(this.now - oldest);
    },

    flash() {
      var val = Math.round(this.now / 1000);
      if (val % 2 == 0) return { backgroundColor: this.conf.flashColors.first };
      else return { backgroundColor: this.conf.flashColors.second };
    },

    allQueues() {
      return this.$store.getters.queueData
        .filter(
          (x) =>
            this.config.queueNumbers.includes(x.queueNumber) &&
            this.config.campaignNames.includes(x.campaign)
        )
        .map((x) => ({
          name: x.queueName,
          calls: x.numberOfCallsInQueue,
          oldestDT: x.oldestCallInQueueDT,
          oldest: x.oldestCallInQueue,
          totalCalls: x.totalCalls,
          abandoned: x.abandoned,
          abandonRate: x.abandonRate,
        }))
        .sort((a, b) => {
          if (this.conf.sortBy === "None") return true;
          if (this.conf.sortBy === "Queueing Calls") return b.calls - a.calls;
          if (this.conf.sortBy === "Total Calls")
            return b.totalCalls - a.totalCalls;
          if (this.conf.sortBy === "Abandoned")
            return b.abandoned - a.abandoned;
          if (this.conf.sortBy === "Abandon Rate") {
            var aAbandon = a.abandonRate.slice(0,-1);
            var bAbandon = b.abandonRate.slice(0,-1);
            return Number(bAbandon) - Number(aAbandon);
          }
        });
    },
  },

  methods: {
    selectSort(selected) {
      this.conf.sortBy = selected;
      this.$emit("configChange", this.conf);
    },

    registerDependencies() {
      this.$store.getters.queueArgs.addProperty("QueueName");
      this.$store.getters.queueArgs.addProperty("Campaign");
      this.$store.getters.queueArgs.addProperty("OldestCallInQueue");
      this.$store.getters.queueArgs.addProperty("TotalCalls");
      this.$store.getters.queueArgs.addProperty("Abandoned");
      this.$store.getters.queueArgs.addProperty("NumberOfCallsInQueue");
      this.$store.getters.queueArgs.addProperty("abandonRate");
    },

    getType(calls) {
      //if (calls == 0) return { backgroundColor: "#FFFFFF" };
      if (this.conf.flash && calls != 0) return this.flash;
      if (this.conf.fixedColor) return this.getColour(calls ?? 0);
    },

    sum(total, num) {
      return total + Math.round(num);
    },

    invert(type) {
      if (type === "fixed") this.conf.flash = false;
      if (type === "flash") this.conf.fixedColor = false;
    },

    count() {
      this.resizedRows += 1;
      if (this.resizedRows == this.allQueues.length) {
        this.resizedRows = 0;
        this.$emit("resized");
      }
    },

    getColour(calls) {
      var color = {};
      var sorted = this.conf.fixedColors;
      if (calls == null) { calls = 0; }
      for (let i = 0; i < sorted.length; i++) {
        if (parseInt(sorted[i].filter) == 0 && calls >= parseInt(sorted[i].volume)) {color = sorted[i].color;}
        else if (parseInt(sorted[i].filter) == 1 && calls == parseInt(sorted[i].volume)) {color = sorted[i].color;}
        else if (parseInt(sorted[i].filter) == 2 && calls <= parseInt(sorted[i].volume)) {color = sorted[i].color;}
        else if (parseInt(sorted[i].filter) == 3){color = sorted[i].color;}
      }
      return { backgroundColor: color };
    },

    //0 = Greater Than or Equal to
    //1 = Equal to
    //2 = Less Than or Equal to
    //3 = Default
    addColor() {
      var currHighest =
        this.conf.fixedColors.length == 0
          ? 0
          : this.conf.fixedColors.map((x) => x.volume).sort((a, b) => b - a);
      this.conf.fixedColors.push({
        volume: parseInt(currHighest) + 1,
        color: "#ffffff",
        filter: "0",
      });
    },

    addStat(stat) {
      if (!this.conf.statsToDisplay) this.conf.statsToDisplay = [];
      this.conf.statsToDisplay.push(stat);
      this.$emit("configChange", this.conf);
    },

    removeStat(stat) {
      if (!this.conf.statsToDisplay) this.conf.statsToDisplay = [];
      var index = this.conf.statsToDisplay.indexOf(stat);
      if (index !== -1) this.conf.statsToDisplay.splice(index, 1);
      this.$emit("configChange", this.conf);
    },

    doResize() {
      // Resize main els
      this.resizeText({
        elements: this.$refs.resize,
      });

      // Resize detail els
      this.resizeText({
        elements: this.$refs["resize-detail"],
      });

      let smallestFont = this.$refs["resize-detail"]
        .map((x) => parseInt(x.style["font-size"].replace("px")))
        .sort((a, b) => a - b)[0];
      this.resizeText({
        elements: this.$refs["resize-detail"],
        maxSize: smallestFont,
      });
    },

    displayStat(stat) {
      if (!this.conf.statsToDisplay) return true;
      else return this.conf.statsToDisplay.includes(stat);
    },
  },
};
</script>

<style scoped>
.default-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  border-radius: inherit;
}

.split-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  white-space: nowrap;
}

.combined-container {
  height: 100%;
  width: 100%;
  border-radius: inherit;
  display: flex;
  flex-direction: column;
}

.flash-animation {
  animation: flash-1 1s ease-in-out infinite;
}

.section {
  flex-grow: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.detail-stats {
  flex-direction: row !important;
}

.detail {
  flex-grow: 1;
  padding: 2px 0;
  box-sizing: border-box;
}
</style>
